/**
 * Secure cache service using Web Crypto API for encryption.
 * Data is encrypted with AES-GCM using a key derived from the access token.
 * This prevents users from modifying cached data even if they inspect localStorage.
 *
 * Security features:
 * - AES-256-GCM encryption (authenticated encryption)
 * - Key derived from access token using PBKDF2
 * - Random IV for each encryption
 * - Timestamp to detect stale data
 * - Automatic invalidation if token changes
 */

import LocalStorageService from "../auth/local-storage-service";

interface SecureCacheEntry {
  iv: string; // Base64 encoded IV
  data: string; // Base64 encoded encrypted data
  ts: number; // Timestamp
  th: string; // Token hash (first 16 chars of token hash for verification)
}

const SALT = new Uint8Array([75, 85, 73, 75, 65, 95, 83, 69, 67, 85, 82, 69, 95, 67, 65, 67, 72, 69]); // "KUIKA_SECURE_CACHE"

class SecureCacheService {
  private static instance: SecureCacheService;

  private keyCache: Map<string, CryptoKey> = new Map();

  private constructor() {}

  public static getInstance(): SecureCacheService {
    if (!SecureCacheService.instance) {
      SecureCacheService.instance = new SecureCacheService();
    }
    return SecureCacheService.instance;
  }

  /**
   * Derive an encryption key from the access token using PBKDF2.
   */
  private async deriveKey(token: string): Promise<CryptoKey> {
    // Check cache first
    const tokenHash = await this.hashToken(token);
    if (this.keyCache.has(tokenHash)) {
      return this.keyCache.get(tokenHash)!;
    }

    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey("raw", encoder.encode(token), "PBKDF2", false, ["deriveKey"]);

    const key = await crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: SALT,
        iterations: 100000,
        hash: "SHA-256"
      },
      keyMaterial,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );

    this.keyCache.set(tokenHash, key);
    return key;
  }

  /**
   * Hash the token to create a verification string.
   */
  private async hashToken(token: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(token);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
    return hashHex.substring(0, 16);
  }

  /**
   * Encrypt and store data securely.
   */
  public async set<T>(key: string, value: T): Promise<boolean> {
    try {
      const token = LocalStorageService.getAccessToken();
      if (!token) {
        return false;
      }

      const cryptoKey = await this.deriveKey(token);
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const encoder = new TextEncoder();
      const plaintext = encoder.encode(JSON.stringify(value));

      const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, cryptoKey, plaintext);

      const entry: SecureCacheEntry = {
        iv: this.arrayBufferToBase64(iv),
        data: this.arrayBufferToBase64(ciphertext),
        ts: Date.now(),
        th: await this.hashToken(token)
      };

      localStorage.setItem(key, JSON.stringify(entry));
      return true;
    } catch (e) {
      // Encryption failed or localStorage unavailable
      return false;
    }
  }

  /**
   * Retrieve and decrypt data securely.
   * Returns null if data is missing, tampered with, or token has changed.
   */
  public async get<T>(key: string): Promise<T | null> {
    try {
      const token = LocalStorageService.getAccessToken();
      if (!token) {
        return null;
      }

      const stored = localStorage.getItem(key);
      if (!stored) {
        return null;
      }

      const entry: SecureCacheEntry = JSON.parse(stored);

      // Verify token hash matches (prevents using old cached data with new token)
      const currentTokenHash = await this.hashToken(token);
      if (entry.th !== currentTokenHash) {
        // Token changed, invalidate cache
        localStorage.removeItem(key);
        return null;
      }

      const cryptoKey = await this.deriveKey(token);
      const iv = this.base64ToArrayBuffer(entry.iv);
      const ciphertext = this.base64ToArrayBuffer(entry.data);

      const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: new Uint8Array(iv) }, cryptoKey, ciphertext);

      const decoder = new TextDecoder();
      const json = decoder.decode(plaintext);
      return JSON.parse(json) as T;
    } catch (e) {
      // Decryption failed - data was tampered with or corrupted
      // Remove the invalid entry
      try {
        localStorage.removeItem(key);
      } catch {}
      return null;
    }
  }

  /**
   * Remove cached data.
   */
  public remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {}
  }

  /**
   * Clear the key cache (call on logout).
   */
  public clearKeyCache(): void {
    this.keyCache.clear();
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }
}

export default SecureCacheService;
